import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Paperclip,
  X,
  Send,
  MapPin,
  Phone,
  Mail,
  Clock,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const MAX_FILES = 3;
const MAX_SIZE = 5 * 1024 * 1024;
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif";

export default function ContactSection({ company, services }) {
  const [form, setForm] = useState({
    nume: "",
    telefon: "",
    email: "",
    vehicul: "",
    serviciu: "",
    mesaj: "",
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [...files];
    for (const f of picked) {
      if (next.length >= MAX_FILES) {
        toast.error(`Maxim ${MAX_FILES} fișiere.`);
        break;
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} depășește 5MB.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((fs) => fs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nume || !form.telefon || !form.email || !form.mesaj) {
      toast.error("Completează câmpurile obligatorii.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post("/contact", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSent(true);
      setForm({ nume: "", telefon: "", email: "", vehicul: "", serviciu: "", mesaj: "" });
      setFiles([]);
      toast.success("Mesaj trimis! Te contactăm în cel mai scurt timp.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la trimitere. Încearcă din nou.");
    } finally {
      setLoading(false);
    }
  };

  const programRows = [
    company.program_weekdays,
    company.program_saturday,
    company.program_sunday,
    company.program_itp,
  ].filter(Boolean);

  return (
    <section id="contact" className="py-20 sm:py-28 bg-[#F4F6F9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-3">
            // Contact & Programări
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F1E36]">
            Hai să rezolvăm problema mașinii tale
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Scrie-ne detaliile și, opțional, atașează poze cu avaria sau talonul.
            Îți răspundem rapid.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-2xl border border-[#E2E8F0] shadow-sm"
          >
            {sent ? (
              <div
                data-testid="contact-form-success-alert"
                className="flex flex-col items-center justify-center text-center py-12"
              >
                <CheckCircle2 className="w-16 h-16 text-[#16A34A] mb-4" />
                <h3 className="font-display text-2xl font-bold text-[#0F1E36]">
                  Mesaj trimis cu succes!
                </h3>
                <p className="mt-2 text-slate-600 max-w-sm">
                  Mulțumim! Echipa SIMSERV te va contacta în cel mai scurt timp.
                </p>
                <Button
                  onClick={() => setSent(false)}
                  variant="outline"
                  className="mt-6 border-[#E2E8F0]"
                >
                  Trimite alt mesaj
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form-container" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="nume" className="font-semibold text-[#0F1E36]">
                      Nume și prenume *
                    </Label>
                    <Input
                      id="nume"
                      data-testid="contact-input-name"
                      value={form.nume}
                      onChange={set("nume")}
                      placeholder="ex: Ion Popescu"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefon" className="font-semibold text-[#0F1E36]">
                      Telefon *
                    </Label>
                    <Input
                      id="telefon"
                      data-testid="contact-input-phone"
                      value={form.telefon}
                      onChange={set("telefon")}
                      placeholder="ex: 0722 123 456"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="font-semibold text-[#0F1E36]">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="ex: ion@email.ro"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vehicul" className="font-semibold text-[#0F1E36]">
                      Vehicul (marcă, model, an)
                    </Label>
                    <Input
                      id="vehicul"
                      data-testid="contact-input-vehicle"
                      value={form.vehicul}
                      onChange={set("vehicul")}
                      placeholder="ex: Dacia Duster 2021"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="serviciu" className="font-semibold text-[#0F1E36]">
                    Tip serviciu
                  </Label>
                  <select
                    id="serviciu"
                    data-testid="contact-select-service"
                    value={form.serviciu}
                    onChange={set("serviciu")}
                    className="mt-1.5 w-full h-10 rounded-md border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F1E36] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/40"
                  >
                    <option value="">Alege un serviciu...</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="mesaj" className="font-semibold text-[#0F1E36]">
                    Descriere problemă / solicitare *
                  </Label>
                  <Textarea
                    id="mesaj"
                    data-testid="contact-textarea-message"
                    value={form.mesaj}
                    onChange={set("mesaj")}
                    placeholder="Descrie simptomele mașinii, zgomote sau serviciul dorit..."
                    rows={4}
                    className="mt-1.5"
                  />
                </div>

                {/* Attachments */}
                <div>
                  <Label className="font-semibold text-[#0F1E36]">
                    Atașamente (opțional)
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Max 3 fișiere, 5MB/fișier. PDF, DOC, XLS, JPG, PNG, GIF.
                  </p>
                  <label
                    htmlFor="file-input"
                    className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-[#E2E8F0] text-slate-500 hover:border-[#FF5A1F] hover:text-[#FF5A1F] cursor-pointer transition-colors duration-200 font-medium text-sm"
                  >
                    <Paperclip className="w-4 h-4" />
                    Adaugă fișiere
                  </label>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept={ACCEPT}
                    onChange={onFiles}
                    data-testid="contact-input-file"
                    className="hidden"
                  />
                  {files.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {files.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between bg-[#F4F6F9] rounded-lg px-3 py-2 text-sm"
                        >
                          <span className="truncate text-[#0F1E36] font-medium">
                            {f.name}{" "}
                            <span className="text-slate-400 font-mono text-xs">
                              ({(f.size / 1024).toFixed(0)} KB)
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            className="text-slate-400 hover:text-[#DC2626] transition-colors duration-200"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit-button"
                  className="w-full bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold text-base py-6"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Trimite mesajul
                </Button>
              </form>
            )}
          </motion.div>

          {/* Info + map */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-5 shadow-sm">
              <InfoRow icon={MapPin} label="Adresă" testId="company-address-info">
                {company.address}
              </InfoRow>
              <a href={`tel:${(company.phone || "").replace(/\s/g, "")}`} data-testid="company-phone-link">
                <InfoRow icon={Phone} label="Telefon" hover>
                  <span className="font-mono">{company.phone}</span>
                </InfoRow>
              </a>
              {company.email ? (
                <a href={`mailto:${company.email}`} data-testid="company-email-link">
                  <InfoRow icon={Mail} label="Email" hover>
                    {company.email}
                  </InfoRow>
                </a>
              ) : null}
              <InfoRow icon={Clock} label="Program" testId="company-schedule-info">
                <span className="space-y-0.5 block">
                  {programRows.map((p, i) => (
                    <span key={i} className="block">
                      {p}
                    </span>
                  ))}
                </span>
              </InfoRow>
            </div>

            {company.maps_embed ? (
              <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-sm h-[280px]">
                <iframe
                  title="Hartă SIMSERV"
                  data-testid="company-google-maps-iframe"
                  src={company.maps_embed}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, children, testId, hover }) {
  return (
    <div
      data-testid={testId}
      className={`flex items-start gap-4 ${hover ? "group cursor-pointer" : ""}`}
    >
      <span
        className={`shrink-0 w-10 h-10 rounded-lg bg-[#F4F6F9] flex items-center justify-center ${
          hover ? "group-hover:bg-[#FF5A1F]" : ""
        } transition-colors duration-200`}
      >
        <Icon
          className={`w-5 h-5 text-[#FF5A1F] ${hover ? "group-hover:text-white" : ""} transition-colors duration-200`}
        />
      </span>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
          {label}
        </p>
        <div className="text-[#0F1E36] font-medium mt-0.5">{children}</div>
      </div>
    </div>
  );
}
